/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, type ReactNode } from "react"

interface TimerSettings {
    soundsEnabled: boolean
    soundType: string
    alertInterval: number
    defaultTargetHours: number
    isAggregationEnabled: boolean
    desktopNotificationsEnabled: boolean
    isPomodoroEnabled?: boolean
    pomodoroDuration?: number
}

const DEFAULT_SETTINGS: TimerSettings = {
    soundsEnabled: true,
    soundType: "classic",
    alertInterval: 30,
    defaultTargetHours: 8,
    isAggregationEnabled: true,
    desktopNotificationsEnabled: false,
    isPomodoroEnabled: false,
    pomodoroDuration: 25,
}

interface TimerSettingsContextType {
    settings: TimerSettings
    updateSettings: (newSettings: Partial<TimerSettings>) => void
}

const TimerSettingsContext = createContext<TimerSettingsContextType | null>(null)

export function TimerSettingsProvider({ children }: { children: ReactNode }) {
    const [settings, setSettings] = useState<TimerSettings>(() => {
        const saved = localStorage.getItem("tempora_timer_settings")
        return saved ? JSON.parse(saved) : DEFAULT_SETTINGS
    })

    useEffect(() => {
        localStorage.setItem("tempora_timer_settings", JSON.stringify(settings))
    }, [settings])

    const updateSettings = (newSettings: Partial<TimerSettings>) => {
        setSettings(prev => ({ ...prev, ...newSettings }))
    }

    return (
        <TimerSettingsContext.Provider value={{ settings, updateSettings }}>
            {children}
        </TimerSettingsContext.Provider>
    )
}

export function useTimerSettings() {
    const context = useContext(TimerSettingsContext)
    if (!context) {
        throw new Error("useTimerSettings must be used within a TimerSettingsProvider")
    }
    return context
}